import { CreditCard } from '@mui/icons-material';
import * as theme from '../../../theme';
import { transactionTypes, cardTypes } from '../../../contexts/TransactionTypeContext';
import dayjs from 'dayjs';
import { db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import logo from '../../../images/tb-logo-dark.png';
import icon from '../../../images/tb-icon.png';

export default function TransactionHistoryItem({ transaction, clients, practitioner }) {
  const getDescription = (type, clientId, scanId) => {
    let desc = transactionTypes[type] || type;
    if (clientId && clients) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        desc = desc.replace('{client: firstName lastName}', `${client.firstName} ${client.lastName}`);
      }
    }
    let fullDesc = <div style={{ fontSize: 14, fontWeight: 500 }}>{desc}</div>;
    if (scanId) {
      fullDesc = (
        <div>
          {fullDesc}
          <div style={{ fontSize: 11, color: theme.Colors.GrayMedium }}>Ref #: {scanId}</div>
        </div>
      );
    }
    return fullDesc;
  };

  const getCard = (brand) => {
    if (brand !== '') {
      return cardTypes[brand] || 'CARD';
    } else {
      return 'CARD';
    }
  }

  const handleClick = async () => {
    let scanDate = null;
    if (transaction.scanId) {
      try {
        const scanRef = doc(db, `clients/${transaction.clientId}/scans/${transaction.scanId}`);
        const scanSnap = await getDoc(scanRef);
        if (!scanSnap.exists()) {
          alert('Failed to fetch scan details');
          return;
        }
        scanDate = scanSnap.data().scanDate;
      } catch (err) {
        alert('Error fetching scan details: ' + err.message);
        return;
      }
    }

    const client = clients.find(c => c.id === transaction.clientId);
    let description = transactionTypes[transaction.type] || transaction.type;
    if (client && transaction.scanId && scanDate) {
      description = description.replace('- {client: firstName lastName}', `for ${client.firstName} ${client.lastName} with a scheduled scan date of ${scanDate}`);
    } else if (client) {
      description = description.replace('{client: firstName lastName}', `${client.firstName} ${client.lastName}`);
    }
    
    let displayAmount = (transaction.amount / 100).toFixed(2);
    if (transaction.type === 'scan-down-refund') {
      displayAmount = `-${displayAmount}`;
    }

    const formattedDate = dayjs(transaction.dateCreated.toDate()).format('MMMM D, YYYY [at] h:mm A');
    const paymentMethod = transaction.card ? `<b>${getCard(transaction.card.brand)}</b> ending in <b>${transaction.card.last4 || '0000'}</b>` : '';

    const practitionerName = practitioner.businessName 
      ? `${practitioner.firstName} ${practitioner.lastName} (${practitioner.businessName})`
      : `${practitioner.firstName} ${practitioner.lastName}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TruBalance Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; background: white; margin: 20px; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { text-align: center; }
          .logo { width: 300px; }
          .icon { width: 70px; margin-top: 40px; }
          .business-info { margin-top: 10px; font-size: 14px; }
          .billed-to { text-align: center; margin-top: 40px; font-size: 14px; }
          .business-name { font-weight: bold; margin-bottom: 6px }
          .details { margin-top: 40px; font-size: 14px; }
          .details p { text-align: center; margin: 8px 0; }
          .payment-details {}
          .payment-details p { text-align: center; }
          .print-btn { position: absolute; top: 20px; right: 20px; padding: 8px 16px; background: ${theme.Colors.Purple}; border-radius: 4px; color: white; border: none; cursor: pointer; }
          .print-btn:hover { background: ${theme.Colors.GoldDark}; }
          @media print { .print-btn { display: none; } body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <img src="${logo}" alt="TruBalance Logo" class="logo">
            <div class="business-info">
              <div class="business-name">TruBalance Bioenergetics</div>
              10800 Old County Rd 15, Plymouth, MN 55441<br>
              hello@trubalancebioenergetics.com
            </div>
          </div>
          <div class="billed-to">
            <div class="business-name">${formattedDate}</div>
          </div>
          <div class="billed-to">
            <div class="business-name">Receipt Number</div>
            ${transaction.id}
          </div>
          <div class="billed-to">
            <div class="business-name">Billed To</div>
            ${practitionerName}
          </div>
          <div class="billed-to">
            <div class="business-name">Description</div>
            ${description}
          </div>
          <div class="details">
            <div class="payment-details">
              <p><strong>Total Paid:</strong> $${displayAmount} USD</p>
              <p>${paymentMethod}</p>
            </div>
          </div>
          <div class="header">
            <img src="${icon}" alt="TruBalance Icon" class="icon">
          </div>
        </div>
        <button class="print-btn" onclick="window.print()">Print</button>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      alert('Popup blocked. Please allow popups to view the receipt.');
    }
  };

  let amount = (transaction.amount / 100).toFixed(2);
  if (transaction.type === 'scan-down-refund') {
    amount = `-${amount}`;
  }
  const date = dayjs(transaction.dateCreated.toDate()).format('MM/DD/YYYY');
  const last4 = transaction.card?.last4 ? `x${transaction.card.last4}` : 'x0000';
  const brand = transaction.card?.brand || '';

  return (
    <div 
      className="cardLink" 
      style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: 8, 
        cursor: 'pointer' 
      }} 
      onClick={handleClick}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginRight: 16 }}>
        <CreditCard style={{ marginRight: 8 }} />
        <div style={{ textAlign: 'left', width: 50 }}>
          <div style={{ fontSize: 12, fontWeight: 500 }}>{last4}</div>
          <div style={{ fontSize: 10, color: theme.Colors.GrayMedium }}>{getCard(brand)}</div>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        {getDescription(transaction.type, transaction.clientId, transaction.scanId)}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginRight: 16 }}>${amount}</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{date}</div>
    </div>
  );
}